/**
 * The distance chips — "25 km · 50 km · 100 km · Hela Sverige" (P25009-425
 * BR-2) — in the filter card's "Avstånd" group, on a computer and in the
 * phone's filter sheet alike.
 *
 * A chip writes `radius` (km) and the buyer's area as `origin`, and the
 * search turns the pair into a box for the API plus a circle trim
 * (SearchPage.duck, radiusSearch.js). The area is the one the page already
 * knows; only when it knows none is the browser asked, once (BR-5). A chip
 * replaces a map viewport or a typed place: one area at a time.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AREA_ERROR_TEXT,
  RADIUS_OPTIONS_KM,
  areaToOriginParam,
  markNearMePromptDone,
  parseRadiusKm,
  resolveBuyerArea,
} from '../../util/buyerArea';

/** The chips, in order; `km: null` is "Hela Sverige". */
export const DISTANCE_CHIPS = [
  ...RADIUS_OPTIONS_KM.map(km => ({ km, label: `${km} km` })),
  { km: null, label: 'Hela Sverige' },
];

/** The pill and group summary for a radius: "Inom 50 km". */
export const radiusLabel = km => (km ? `Inom ${km} km` : null);

/**
 * The search string after choosing a chip.
 * @param {String} search the current location.search
 * @param {Number|null} km the chip; null = "Hela Sverige"
 * @param {{lat, lng}} [area] the buyer's area (needed for a km chip)
 */
export const searchWithRadius = (search, km, area) => {
  const params = new URLSearchParams(search || '');
  params.delete('page');
  if (!km) {
    params.delete('radius');
  } else {
    params.set('radius', String(km));
    params.set('origin', areaToOriginParam(area));
    params.delete('bounds');
    params.delete('mapSearch');
    params.delete('address');
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * State and handler for the chips.
 * @returns {{ radiusKm: Number|null, select: Function, busy: Boolean, notice: String|null }}
 */
export const useDistanceFilter = () => {
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector(state => state.user?.currentUser || null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const radiusKm = parseRadiusKm(new URLSearchParams(location.search || '').get('radius'));

  const select = km => {
    setNotice(null);
    const search = history.location.search;
    if (!km) {
      history.push({ search: searchWithRadius(search, null) });
      return;
    }
    const urlOrigin = new URLSearchParams(search || '').get('origin');
    setBusy(true);
    resolveBuyerArea({ urlOrigin, currentUser })
      .then(area => {
        markNearMePromptDone('answered');
        setBusy(false);
        history.push({ search: searchWithRadius(history.location.search, km, area) });
      })
      .catch(err => {
        setBusy(false);
        setNotice(AREA_ERROR_TEXT[err?.reason] || AREA_ERROR_TEXT.unavailable);
      });
  };

  return { radiusKm, select, busy, notice };
};
